import axios from "axios";
import { get_headers, KYC_VERIFICATION_URL, KYC_VERIFIED_DATA } from "@/utils/constants/constants.js";

/**
 * Parses error responses from Axios and returns a user-friendly message.
 * @param {Object} error - Axios error object
 * @param {string} fallbackMessage - Default message if no specific error is found
 * @returns {string} - Parsed error message
 */
function parse_kyc_error(error, fallbackMessage) {
  let error_message = fallbackMessage;

  if (error.response) {
    const data = error.response.data;
    if (data?.message) {
      error_message = data.message;
    } else if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        error_message = parsed.message || error_message;
      } catch {
        error_message = data || error_message;
      }
    }
  } else if (error.request) {
    error_message = 'No response from verification service';
  } else {
    error_message = error.message;
  }

  return error_message;
}

/**
 * KYC Verification API function
 * @param {Object} payload - Contains service_id, form_id, application_id
 * @returns {Promise} - API response
 */
export const kycVerify = async (payload) => {
  const headers = get_headers();
    const response = await axios.post(KYC_VERIFICATION_URL, payload, { headers });
    return typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
};

/**
 * Get KYC Verification Results
 * @param {Object} params - Contains entity_profile_id and service_id
 * @returns {Promise} - API response with verification results
 */
export const getKycVerificationResults = async (params = {}) => {
  const headers = get_headers();

  try {
    const entity_profile_id = params.entity_profile_id || sessionStorage.getItem("entity_profile_id");
    const service_id = params.service_id;
    if (!entity_profile_id || !service_id) return;

    const response = await axios.get(KYC_VERIFIED_DATA, {
      headers,
      params: { entity_profile_id, service_id },
    });

    const data = typeof response.data === "string" ? JSON.parse(response.data) : response.data;

    // Detect real success/fail keywords
    const status_text = (data.status || "").toLowerCase();
    const normalized_status =
      status_text.includes("success") ||
      status_text.includes("pass") ||
      status_text.includes("verified") ||
      status_text.includes("completed")
        ? "success"
        : "fail";


    const result_object = {
      status: normalized_status,
      message:
        data.message ||
        (normalized_status === "fail" ? "System verification failed." : ""),
      field_results: data.field_results || {},
      results_data: data.results_data || data.data || data.details || {},
      service_name: "KYC Verification",
    };


    return {
      status: normalized_status,
      results: [result_object],
      raw_data: data,
      service_name: "KYC Verification",
    };
  } catch (error) {
    this.show_error(parse_kyc_error(error, 'Failed to fetch verification results'));
  }
};